import type { CvrBaseline, DailyPoint } from "../api";

type Props = {
  rate: number | null;
  daily: DailyPoint[];
  cvrBaseline: CvrBaseline | null;
  // The server's suppression floor, echoed so the label states the real number.
  // NEVER a locally-defaulted literal: this component hardcoded the string
  // "n<30", so if CVR_MIN_ORDERS moved to 50 the hero would have announced a
  // floor of 30 over cells the server suppressed at 50. `null` means "no number
  // to state", and the label then states none -- same contract as
  // ResponseBreakdownRow. The UI does NOT apply the threshold; `cvr === null`
  // is and remains the only suppression signal.
  cvrMinOrders: number | null;
};

const W = 320;
const H = 56;

export function ConversionHero({
  rate,
  daily,
  cvrBaseline,
  cvrMinOrders,
}: Props) {
  // Conversion rate is undefined on days with zero traffic (0/0). Render those
  // as gaps in the line rather than silently dropping them, so the sparkline
  // distinguishes "no traffic" from "traffic but no conversions".
  const ratios: (number | null)[] = daily.map((d) =>
    d.visitors > 0 ? Math.min(1, d.submits / d.visitors) : null,
  );
  const validRatios = ratios.filter((v): v is number => v !== null);
  const maxRatio = validRatios.length > 0 ? Math.max(0.01, ...validRatios) : 1;

  const denom = Math.max(1, daily.length - 1);
  const segments: string[] = [];
  let current = "";
  ratios.forEach((v, i) => {
    if (v === null) {
      if (current) segments.push(current);
      current = "";
      return;
    }
    const x = (i / denom) * W;
    const y = H - (v / maxRatio) * (H - 12) - 6;
    if (!current) {
      current = `M ${x.toFixed(2)} ${y.toFixed(2)}`;
    } else {
      current += ` L ${x.toFixed(2)} ${y.toFixed(2)}`;
    }
  });
  if (current) segments.push(current);

  const display = rate === null ? "—" : `${rate.toFixed(rate >= 100 ? 0 : 2)}%`;

  return (
    <div className="rounded-2xl border border-mars-orange/40 bg-mars-orange p-5 text-mars-black shadow-[0_0_0_1px_rgba(255,86,0,0.2)]">
      <div className="mono text-[0.65rem] font-bold uppercase tracking-[0.18em] text-mars-black/80">
        Conversion Rate
      </div>
      <div className="mono mt-3 text-4xl font-bold tabular-nums">
        {rate === null ? "—" : <>&Oslash; {display}</>}
      </div>
      {segments.length > 0 ? (
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="mt-4 h-12 w-full"
          aria-hidden="true"
        >
          {segments.map((d, i) => (
            <path
              key={i}
              d={d}
              fill="none"
              stroke="#000"
              strokeOpacity={0.85}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
        </svg>
      ) : (
        <div className="mt-4 h-12 rounded bg-mars-black/10" aria-hidden="true" />
      )}
      {cvrBaseline !== null && (
        <div className="mt-4 border-t border-mars-black/20 pt-3">
          <div className="mono text-[0.6rem] font-bold uppercase tracking-[0.18em] text-mars-black/70">
            Purchase CVR
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span
              className="mono text-xl font-bold tabular-nums"
              data-testid="quiz-cvr"
            >
              {cvrBaseline.cvr === null
                ? cvrMinOrders === null
                  ? "too few"
                  : `n<${cvrMinOrders}`
                : `${cvrBaseline.cvr.toFixed(2)}%`}
            </span>
            <span
              className="mono text-[0.65rem] uppercase tracking-[0.12em] text-mars-black/60"
              data-testid="quiz-orders"
            >
              {cvrBaseline.orders.toLocaleString("en-US")} orders
            </span>
          </div>
          <div className="mono mt-1 text-[0.55rem] leading-snug text-mars-black/60">
            Orders within 24h of the quiz, matched first-party. Not comparable
            to Triple Whale.
          </div>
        </div>
      )}
    </div>
  );
}
